import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { useAuthStore } from '../../stores/auth.store';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  FileText,
  Download,
  Filter,
  ArrowDownLeft,
  ArrowUpRight,
  Calendar,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  User,
  Copy,
  Check,
  Receipt,
  Loader2,
} from 'lucide-react';

type TransactionType = 'ALL' | 'PIX_IN' | 'PIX_OUT' | 'TRANSFER_IN' | 'TRANSFER_OUT' | 'FEE' | 'DEPOSIT' | 'WITHDRAWAL' | 'REFUND';

interface StatementFilters {
  startDate: string;
  endDate: string;
  type: TransactionType;
  minAmount: string;
  maxAmount: string;
  search: string;
}

interface StatementEntry {
  id: string;
  type: string;
  transactionType?: string;
  description: string;
  amount: string;
  fee: string;
  netAmount: string;
  balanceAfter: string;
  createdAt: string;
  senderName?: string | null;
  senderDocument?: string | null;
  correlationId?: string | null;
  endToEndId?: string | null;
  metadata?: Record<string, any>;
}

export function CustomerStatement() {
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [selectedEntry, setSelectedEntry] = useState<StatementEntry | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const { user } = useAuthStore();
  const [filters, setFilters] = useState<StatementFilters>({
    startDate: '', endDate: '', type: 'ALL', minAmount: '', maxAmount: '', search: '',
  });

  const { data: statementData, isLoading } = useQuery({
    queryKey: ['statement', page, filters],
    queryFn: async () => {
      const skip = (page - 1) * pageSize;
      const params: any = { skip, take: pageSize };
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.type !== 'ALL') params.transactionType = filters.type;
      if (filters.minAmount) params.minAmount = parseAmount(filters.minAmount);
      if (filters.maxAmount) params.maxAmount = parseAmount(filters.maxAmount);
      if (filters.search) params.search = filters.search;
      const response = await api.get('/banking/statement', { params });
      return response.data;
    },
  });

  const { data: balanceData } = useQuery({
    queryKey: ['banking', 'balance'],
    queryFn: async () => {
      const response = await api.get('/banking/balance');
      return response.data;
    },
  });

  const fmt = (value: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value / 100);

  const fmtDate = (date: string) => {
    if (!date) return '';
    const d = new Date(date);
    return isNaN(d.getTime()) ? '' : new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d);
  };

  const fmtDateShort = (date: string) => {
    if (!date) return '';
    const d = new Date(date);
    return isNaN(d.getTime()) ? '' : new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' }).format(d);
  };

  const parseAmount = (value: string): number => parseInt(value.replace(/\D/g, ''), 10) || 0;

  const handleAmountChange = (value: string, field: 'minAmount' | 'maxAmount') => {
    const cents = parseInt(value.replace(/\D/g, ''), 10) || 0;
    const formatted = (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    setFilters({ ...filters, [field]: formatted });
  };

  const handleExportPdf = useCallback(async () => {
    setExportingPdf(true);
    try {
      // Fetch all entries for the current filters (up to 500)
      const params: any = { skip: 0, take: 500 };
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.type !== 'ALL') params.transactionType = filters.type;
      if (filters.minAmount) params.minAmount = parseAmount(filters.minAmount);
      if (filters.maxAmount) params.maxAmount = parseAmount(filters.maxAmount);
      if (filters.search) params.search = filters.search;
      const response = await api.get('/banking/statement', { params });
      const allEntries: StatementEntry[] = response.data?.entries || [];

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();

      // Header background
      doc.setFillColor(15, 23, 42); // surface-900
      doc.rect(0, 0, pageWidth, 45, 'F');

      // Company name
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      const appName = import.meta.env.VITE_APP_NAME || 'Hub Business';
      doc.text(appName, 14, 18);

      // Title
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Extrato de Movimentações', 14, 28);

      // Account info
      doc.setFontSize(9);
      doc.setTextColor(148, 163, 184); // surface-400
      const accountName = user?.name || 'Correntista';
      const now = new Date();
      doc.text(`Titular: ${accountName}`, 14, 37);
      doc.text(`Gerado em: ${now.toLocaleDateString('pt-BR')} às ${now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`, 14, 42);

      // Period info on right
      if (filters.startDate || filters.endDate) {
        const periodText = `Período: ${filters.startDate ? new Date(filters.startDate + 'T00:00:00').toLocaleDateString('pt-BR') : 'início'} a ${filters.endDate ? new Date(filters.endDate + 'T00:00:00').toLocaleDateString('pt-BR') : 'hoje'}`;
        doc.text(periodText, pageWidth - 14, 37, { align: 'right' });
      }

      // Summary cards
      const totalIn = safeInt(response.data?.summary?.totalIn);
      const totalOut = safeInt(response.data?.summary?.totalOut);
      const balance = safeInt(balanceData?.availableBalance);

      doc.setFillColor(30, 41, 59); // surface-800
      doc.roundedRect(14, 50, 55, 20, 2, 2, 'F');
      doc.roundedRect(75, 50, 55, 20, 2, 2, 'F');
      doc.roundedRect(136, 50, 55, 20, 2, 2, 'F');

      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text('Saldo Disponível', 18, 57);
      doc.text('Total Entradas', 79, 57);
      doc.text('Total Saídas', 140, 57);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255);
      doc.text(fmt(balance), 18, 65);
      doc.setTextColor(74, 222, 128); // green-400
      doc.text(`+${fmt(totalIn)}`, 79, 65);
      doc.setTextColor(248, 113, 113); // red-400
      doc.text(`-${fmt(totalOut)}`, 140, 65);

      // Table
      const tableData = allEntries.map((entry) => {
        const credit = isCredit(entry);
        const destKey = entry.metadata?.destinationKey;
        const senderName = getSenderDisplay(entry);
        const counterparty = credit
          ? (senderName || '-')
          : (destKey || senderName || '-');

        return [
          fmtDate(entry.createdAt),
          getLabel(entry),
          credit ? `De: ${counterparty}` : `Para: ${counterparty}`,
          `${credit ? '+' : '-'}${fmt(safeInt(entry.amount))}`,
          fmt(safeInt(entry.balanceAfter)),
        ];
      });

      autoTable(doc, {
        startY: 78,
        head: [['Data/Hora', 'Tipo', 'Origem/Destino', 'Valor', 'Saldo']],
        body: tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [30, 41, 59],
          textColor: [148, 163, 184],
          fontSize: 8,
          fontStyle: 'bold',
          halign: 'left',
        },
        bodyStyles: {
          fontSize: 7.5,
          textColor: [226, 232, 240],
          fillColor: [15, 23, 42],
          lineColor: [51, 65, 85],
          lineWidth: 0.1,
        },
        alternateRowStyles: {
          fillColor: [30, 41, 59],
        },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { cellWidth: 35 },
          2: { cellWidth: 50 },
          3: { cellWidth: 30, halign: 'right' },
          4: { cellWidth: 30, halign: 'right' },
        },
        margin: { left: 14, right: 14 },
        didParseCell: (data: any) => {
          // Color amounts green/red
          if (data.section === 'body' && data.column.index === 3) {
            const text = data.cell.raw as string;
            if (text.startsWith('+')) {
              data.cell.styles.textColor = [74, 222, 128];
            } else if (text.startsWith('-')) {
              data.cell.styles.textColor = [248, 113, 113];
            }
          }
        },
      });

      // Footer on each page
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setFillColor(15, 23, 42);
        doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
        doc.setFontSize(7);
        doc.setTextColor(100, 116, 139);
        doc.text(`${appName} - Extrato Confidencial`, 14, pageHeight - 6);
        doc.text(`Página ${i} de ${pageCount}`, pageWidth - 14, pageHeight - 6, { align: 'right' });
      }

      // Download
      const fileName = `extrato_${now.toISOString().slice(0, 10)}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
    } finally {
      setExportingPdf(false);
    }
  }, [filters, balanceData, user]);

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  const isCredit = (entry: any) => entry.type === 'CREDIT';
  const safeInt = (val: any): number => { const n = typeof val === 'string' ? parseInt(val, 10) : Number(val); return isNaN(n) ? 0 : n; };

  const getLabel = (entry: any) => {
    const labels: Record<string, string> = {
      PIX_IN: 'Pix Recebido', PIX_OUT: 'Pix Enviado', TRANSFER_IN: 'Transferência Recebida',
      TRANSFER_OUT: 'Transferência Enviada', FEE: 'Taxa', DEPOSIT: 'Depósito',
      WITHDRAWAL: 'Saque', REFUND: 'Estorno',
    };
    return labels[entry.transactionType] || entry.description || entry.type;
  };

  const getSenderDisplay = (entry: any) => {
    // Priority: senderName > metadata.customerName > metadata.source (but not raw source names)
    if (entry.senderName && entry.senderName !== 'ANYPAY_SYNC' && entry.senderName !== 'ANYPAY') return entry.senderName;
    if (entry.metadata?.customerName) return entry.metadata.customerName;
    // Don't show raw source names like ANYPAY_SYNC
    return null;
  };

  const transactionTypes = [
    { value: 'ALL', label: 'Todos' }, { value: 'PIX_IN', label: 'Pix Recebido' },
    { value: 'PIX_OUT', label: 'Pix Enviado' }, { value: 'FEE', label: 'Taxa' },
    { value: 'TRANSFER_IN', label: 'Transferência Recebida' }, { value: 'TRANSFER_OUT', label: 'Transferência Enviada' },
    { value: 'DEPOSIT', label: 'Depósito' }, { value: 'WITHDRAWAL', label: 'Saque' }, { value: 'REFUND', label: 'Estorno' },
  ];

  const totalEntries = statementData?.pagination?.total || 0;
  const totalPages = Math.max(1, Math.ceil(totalEntries / pageSize));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Extrato</h1>
          <p className="text-surface-400">Histórico de movimentações</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => setShowFilters(!showFilters)}>
            <Filter size={16} className="mr-2" />Filtros
          </Button>
          <Button variant="secondary" size="sm" onClick={handleExportPdf} disabled={exportingPdf || isLoading}>
            {exportingPdf ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}
            {exportingPdf ? 'Gerando...' : 'Exportar PDF'}
          </Button>
        </div>
      </div>

      {/* Balance Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4">
          <p className="text-xs text-surface-400 mb-1">Saldo Disponível</p>
          <p className="text-xl font-bold text-white">{fmt(safeInt(balanceData?.availableBalance))}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-surface-400 mb-1">Entradas (período)</p>
          <p className="text-xl font-bold text-green-400">+{fmt(safeInt(statementData?.summary?.totalIn))}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs text-surface-400 mb-1">Saídas (período)</p>
          <p className="text-xl font-bold text-red-400">-{fmt(safeInt(statementData?.summary?.totalOut))}</p>
        </Card>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <div className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <Input label="Data inicial" type="date" value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} />
              <Input label="Data final" type="date" value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} />
              <Select label="Tipo" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value as TransactionType })} options={transactionTypes} />
              <Input label="Valor mínimo" placeholder="0,00" value={filters.minAmount} onChange={(e) => handleAmountChange(e.target.value, 'minAmount')} />
              <Input label="Valor máximo" placeholder="0,00" value={filters.maxAmount} onChange={(e) => handleAmountChange(e.target.value, 'maxAmount')} />
              <Input label="Buscar" placeholder="Descrição..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} leftIcon={<Search size={16} className="text-surface-400" />} />
            </div>
            <div className="flex justify-end mt-4 gap-2">
              <Button variant="ghost" size="sm" onClick={() => setFilters({ startDate: '', endDate: '', type: 'ALL', minAmount: '', maxAmount: '', search: '' })}>Limpar</Button>
              <Button variant="primary" size="sm" onClick={() => setPage(1)}>Aplicar</Button>
            </div>
          </div>
        </Card>
      )}

      {/* Statement List */}
      <Card>
        <div className="p-6 flex flex-col" style={{ maxHeight: 'calc(100vh - 320px)' }}>
          {isLoading ? (
            <div className="space-y-3">
              {[1,2,3,4,5,6].map((i) => (
                <div key={i} className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 animate-pulse">
                  <div className="w-10 h-10 rounded-full bg-surface-700" />
                  <div className="flex-1"><div className="h-4 w-32 bg-surface-700 rounded mb-2" /><div className="h-3 w-24 bg-surface-700 rounded" /></div>
                  <div className="text-right"><div className="h-4 w-20 bg-surface-700 rounded mb-2" /><div className="h-3 w-16 bg-surface-700 rounded" /></div>
                </div>
              ))}
            </div>
          ) : statementData?.entries?.length > 0 ? (
            <>
              <div className="space-y-2 overflow-y-auto flex-1 pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#334155 transparent' }}>
                {statementData.entries.map((entry: StatementEntry, index: number) => {
                  const entryDate = entry.createdAt;
                  const prevDate = index > 0 ? statementData.entries[index - 1].createdAt : null;
                  const showDateHeader = index === 0 || new Date(entryDate).toDateString() !== new Date(prevDate!).toDateString();
                  const amount = safeInt(entry.amount);
                  const sender = getSenderDisplay(entry);
                  const destKey = entry.metadata?.destinationKey;
                  const counterparty = isCredit(entry)
                    ? (sender || 'Recebido')
                    : (destKey || sender || null);

                  return (
                    <div key={entry.id}>
                      {showDateHeader && (
                        <div className="flex items-center gap-2 py-2 mt-4 first:mt-0 sticky top-0 bg-surface-900/95 backdrop-blur-sm z-10">
                          <Calendar size={14} className="text-surface-500" />
                          <span className="text-xs font-medium text-surface-400">{fmtDateShort(entryDate)}</span>
                          <div className="flex-1 h-px bg-surface-800" />
                        </div>
                      )}
                      <div
                        onClick={() => setSelectedEntry(entry)}
                        className="flex items-center gap-4 p-3 rounded-lg bg-surface-800/50 hover:bg-surface-700/50 transition-colors cursor-pointer"
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isCredit(entry) ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                          {isCredit(entry) ? <ArrowDownLeft size={20} className="text-green-400" /> : <ArrowUpRight size={20} className="text-red-400" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">{getLabel(entry)}</p>
                          {counterparty && (
                            <p className="text-xs text-primary-400 flex items-center gap-1 truncate">
                              <User size={12} />
                              <span className="truncate">{isCredit(entry) ? 'De' : 'Para'}: {counterparty}</span>
                            </p>
                          )}
                          <p className="text-xs text-surface-500">{fmtDate(entryDate)}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className={`text-sm font-semibold ${isCredit(entry) ? 'text-green-400' : 'text-red-400'}`}>
                            {isCredit(entry) ? '+' : '-'}{fmt(amount)}
                          </p>
                          <p className="text-xs text-surface-500">Saldo: {fmt(safeInt(entry.balanceAfter))}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {totalEntries > pageSize && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-surface-800 flex-shrink-0">
                  <p className="text-sm text-surface-400">
                    {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalEntries)} de {totalEntries}
                  </p>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setPage(page - 1)} disabled={page === 1}><ChevronLeft size={16} /></Button>
                    <span className="flex items-center px-3 text-sm text-surface-300">{page} / {totalPages}</span>
                    <Button variant="ghost" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages}><ChevronRight size={16} /></Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto text-surface-600 mb-3" />
              <p className="text-surface-400">Nenhuma movimentação encontrada</p>
            </div>
          )}
        </div>
      </Card>

      {/* Receipt/Comprovante Modal */}
      {selectedEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setSelectedEntry(null)}>
          <div className="bg-surface-900 rounded-2xl w-full max-w-md border border-surface-700 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-surface-800">
              <div className="flex items-center gap-2">
                <Receipt size={20} className="text-primary-400" />
                <h3 className="text-lg font-semibold text-white">Comprovante</h3>
              </div>
              <button onClick={() => setSelectedEntry(null)} className="p-1 rounded-lg hover:bg-surface-800 transition-colors">
                <X size={20} className="text-surface-400" />
              </button>
            </div>

            {/* Content */}
            <div className="p-5 space-y-4">
              {/* Type & Amount */}
              <div className="text-center py-3">
                <p className="text-sm text-surface-400 mb-1">{getLabel(selectedEntry)}</p>
                <p className={`text-3xl font-bold ${isCredit(selectedEntry) ? 'text-green-400' : 'text-red-400'}`}>
                  {isCredit(selectedEntry) ? '+' : '-'}{fmt(safeInt(selectedEntry.amount))}
                </p>
              </div>

              <div className="space-y-3 bg-surface-800/50 rounded-xl p-4">
                {/* Sender/Receiver */}
                {getSenderDisplay(selectedEntry) && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-surface-400">{isCredit(selectedEntry) ? 'Remetente' : 'Destinatário'}</span>
                    <span className="text-sm text-white font-medium">{getSenderDisplay(selectedEntry)}</span>
                  </div>
                )}

                {/* Sender Document */}
                {(selectedEntry.senderDocument || selectedEntry.metadata?.customerDocument) && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-surface-400">CPF/CNPJ</span>
                    <span className="text-sm text-white font-mono">
                      {selectedEntry.senderDocument || selectedEntry.metadata?.customerDocument}
                    </span>
                  </div>
                )}

                {/* Date */}
                <div className="flex justify-between items-center">
                  <span className="text-xs text-surface-400">Data/Hora</span>
                  <span className="text-sm text-white">{fmtDate(selectedEntry.createdAt)}</span>
                </div>

                {/* Description */}
                {selectedEntry.description && (
                  <div className="flex justify-between items-start">
                    <span className="text-xs text-surface-400">Descrição</span>
                    <span className="text-sm text-white text-right max-w-[200px]">{selectedEntry.description}</span>
                  </div>
                )}

                {/* Fee */}
                {safeInt(selectedEntry.fee) > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-surface-400">Taxa</span>
                    <span className="text-sm text-yellow-400">{fmt(safeInt(selectedEntry.fee))}</span>
                  </div>
                )}

                {/* Balance After */}
                <div className="flex justify-between items-center border-t border-surface-700 pt-3">
                  <span className="text-xs text-surface-400">Saldo após</span>
                  <span className="text-sm text-white font-semibold">{fmt(safeInt(selectedEntry.balanceAfter))}</span>
                </div>
              </div>

              {/* IDs */}
              <div className="space-y-2 bg-surface-800/30 rounded-xl p-4">
                {selectedEntry.endToEndId && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-surface-500">End-to-End ID</span>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-surface-400 font-mono truncate max-w-[180px]">{selectedEntry.endToEndId}</span>
                      <button onClick={() => copyToClipboard(selectedEntry.endToEndId!, 'e2e')} className="p-1 hover:bg-surface-700 rounded">
                        {copied === 'e2e' ? <Check size={12} className="text-green-400" /> : <Copy size={12} className="text-surface-500" />}
                      </button>
                    </div>
                  </div>
                )}
                {selectedEntry.correlationId && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-surface-500">ID Transação</span>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-surface-400 font-mono truncate max-w-[180px]">{selectedEntry.correlationId}</span>
                      <button onClick={() => copyToClipboard(selectedEntry.correlationId!, 'corr')} className="p-1 hover:bg-surface-700 rounded">
                        {copied === 'corr' ? <Check size={12} className="text-green-400" /> : <Copy size={12} className="text-surface-500" />}
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-xs text-surface-500">ID Registro</span>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-surface-400 font-mono truncate max-w-[180px]">{selectedEntry.id}</span>
                    <button onClick={() => copyToClipboard(selectedEntry.id, 'id')} className="p-1 hover:bg-surface-700 rounded">
                      {copied === 'id' ? <Check size={12} className="text-green-400" /> : <Copy size={12} className="text-surface-500" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerStatement;
